import {Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {loggerFactory} from "@cm/browser-log"
import {DEFAULT_FLOAT_TEXTURE_TYPE, getAuthoredMaterialSide} from "@template-editor/helpers/three-utils"
import {Subject} from "rxjs"
import {getJitterVector, getReceiverJitterVector} from "@app/template-editor/helpers/jitter"
import {getUVGeometryAnalysis, islandPaddingTexels, layoutUVAtlas, UVGeometryAnalysis, writeAtlasUVs} from "@template-editor/helpers/uv-shadow-map/geometry"
import {
    getOriginalLightRenderData,
    getOriginalMeshRenderData,
    getOriginalObjectRenderData,
    OriginalLightRenderData,
    OriginalMeshRenderData,
    OriginalObjectRenderData,
    restoreOriginalLightRenderData,
    restoreOriginalMeshRenderData,
    restoreOriginalObjectRenderData,
    ThreeLightWithShadow,
} from "@template-editor/helpers/uv-shadow-map/render-state"
import {
    createShadowLightProxy,
    getSceneBoundingSphere,
    registerShadowLightProxies,
    ShadowLightProxy,
} from "@template-editor/helpers/uv-shadow-map/light-proxies"
import {getDummyMaterial, getMaskMaterial, getShadowMaterial} from "@template-editor/helpers/uv-shadow-map/materials"

export class ThreeProgressiveUVShadowMap {
    private uvShadowMapTemp: THREE.RenderTarget
    private uvShadowMap: THREE.RenderTarget
    private shadowMapMask: THREE.RenderTarget
    private shadowMapMaskDirty = false
    private currentMask: THREE.RenderTarget | undefined
    private currentMaskTemp: THREE.RenderTarget | undefined
    private previousMap = TSL.texture()
    private previousMask = TSL.texture()
    private blendMode = TSL.uniform(-1)
    private iteration = TSL.uniform(-1)
    private uvNode = TSL.uv(3)
    private dummyRenderTarget: THREE.RenderTarget = new THREE.RenderTarget(1, 1)
    private shadowMaterials: {[key in THREE.Side]: THREE.Material}
    private dummyMaterials: {[key in THREE.Side]: THREE.Material}
    private maskMaterials: {[key in THREE.Side]: THREE.Material}
    private meshes: Set<THREE.Mesh<THREE.BufferGeometry, THREE.Material>> = new Set()
    private shadowProxies = new Map<THREE.Light, THREE.Light & ShadowLightProxy>()
    private previousShadowLightKey = ""
    private debugMaterial: THREE.MeshBasicMaterial
    private camera = new THREE.PerspectiveCamera()
    private debugObject: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> | undefined = undefined
    private smoothIterations = 0
    private resolutionX = TSL.uniform(-1)
    private resolutionY = TSL.uniform(-1)
    private receiverJitter = TSL.uniform(new THREE.Vector2())
    private blurPlane: THREE.Mesh<THREE.PlaneGeometry, THREE.Material>

    private detachedUVShadowMap: Subject<THREE.Mesh<THREE.BufferGeometry, THREE.Material>> = new Subject<THREE.Mesh<THREE.BufferGeometry, THREE.Material>>()
    detachedUVShadowMap$ = this.detachedUVShadowMap.asObservable()

    private attachedUVShadowMap: Subject<THREE.Mesh<THREE.BufferGeometry, THREE.Material>> = new Subject<THREE.Mesh<THREE.BufferGeometry, THREE.Material>>()
    attachedUVShadowMap$ = this.attachedUVShadowMap.asObservable()

    constructor(
        private renderer: THREE.WebGPURenderer,
        private resolution: number,
    ) {
        registerShadowLightProxies(this.renderer)

        this.uvShadowMapTemp = new THREE.RenderTarget(1, 1, {
            type: DEFAULT_FLOAT_TEXTURE_TYPE,
            generateMipmaps: false,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RedFormat,
            colorSpace: THREE.NoColorSpace,
        })
        this.uvShadowMap = new THREE.RenderTarget(1, 1, {
            type: DEFAULT_FLOAT_TEXTURE_TYPE,
            generateMipmaps: false,
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RedFormat,
            colorSpace: THREE.NoColorSpace,
        })
        this.uvShadowMap.texture.channel = 3
        this.uvShadowMap.texture.flipY = false

        this.shadowMapMask = new THREE.RenderTarget(1, 1, {
            generateMipmaps: false,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RedFormat,
            colorSpace: THREE.NoColorSpace,
        })

        this.debugMaterial = new THREE.MeshBasicMaterial({map: this.uvShadowMap.texture})

        const receiverJitterUV = this.receiverJitter.div(TSL.vec2(this.resolutionX, this.resolutionY))

        this.shadowMaterials = {
            [THREE.FrontSide]: getShadowMaterial(THREE.FrontSide, this.uvNode, this.previousMap, this.iteration, receiverJitterUV),
            [THREE.BackSide]: getShadowMaterial(THREE.BackSide, this.uvNode, this.previousMap, this.iteration, receiverJitterUV),
            [THREE.DoubleSide]: getShadowMaterial(THREE.DoubleSide, this.uvNode, this.previousMap, this.iteration, receiverJitterUV),
        }

        this.dummyMaterials = {
            [THREE.FrontSide]: getDummyMaterial(THREE.FrontSide),
            [THREE.BackSide]: getDummyMaterial(THREE.BackSide),
            [THREE.DoubleSide]: getDummyMaterial(THREE.DoubleSide),
        }

        this.maskMaterials = {
            [THREE.FrontSide]: getMaskMaterial(THREE.FrontSide, this.uvNode),
            [THREE.BackSide]: getMaskMaterial(THREE.BackSide, this.uvNode),
            [THREE.DoubleSide]: getMaskMaterial(THREE.DoubleSide, this.uvNode),
        }

        const blurMaterial = new THREE.NodeMaterial()
        blurMaterial.vertexNode = TSL.vec4(TSL.sub(TSL.uv(), TSL.vec2(0.5)).mul(2), 1, 1)

        const uvNode = TSL.uv().flipY()
        const pixelOffsetX = TSL.float(1.0).div(this.resolutionX)
        const pixelOffsetY = TSL.float(1.0).div(this.resolutionY)

        const resolve = TSL.Fn(() => {
            const result = TSL.vec4(1.0).toVar()

            TSL.If(this.blendMode.equal(0.0), () => {
                TSL.If(this.previousMask.sample(uvNode).r.greaterThan(0.0), () => {
                    result.assign(this.previousMap.sample(uvNode))
                }).Else(() => {
                    const sumColor = TSL.vec4(0.0).toVar()
                    const sumMask = TSL.float(0.0).toVar()

                    TSL.Loop({type: "int", start: -1, end: 2}, {type: "int", start: -1, end: 2}, ({i, j}) => {
                        const offsetUV = uvNode.add(TSL.vec2(pixelOffsetX.mul(i), pixelOffsetY.mul(j)))
                        const mask = this.previousMask.sample(offsetUV).r
                        TSL.If(mask.greaterThan(0.0), () => {
                            sumColor.addAssign(this.previousMap.sample(offsetUV))
                            sumMask.addAssign(1)
                        })
                    })

                    TSL.If(sumMask.greaterThan(0.0), () => {
                        result.assign(sumColor.div(sumMask))
                    })
                })
            })
                .ElseIf(this.blendMode.equal(1.0), () => {
                    const sumColor = TSL.vec4(0.0).toVar()
                    const sumMask = TSL.float(0.0).toVar()

                    TSL.Loop({type: "int", start: -1, end: 2}, {type: "int", start: -1, end: 2}, ({i, j}) => {
                        const offsetUV = uvNode.add(TSL.vec2(pixelOffsetX.mul(i), pixelOffsetY.mul(j)))
                        const mask = this.previousMask.sample(offsetUV)
                        sumMask.addAssign(mask)
                        sumColor.addAssign(this.previousMap.sample(offsetUV).mul(mask))
                    })

                    TSL.If(sumMask.greaterThan(0.0), () => {
                        result.assign(sumColor.div(sumMask))
                    })
                })
                .Else(() => {
                    TSL.Loop({type: "int", start: -1, end: 2}, {type: "int", start: -1, end: 2}, ({i, j}) => {
                        const offsetUV = uvNode.add(TSL.vec2(pixelOffsetX.mul(i), pixelOffsetY.mul(j)))
                        const mask = this.previousMask.sample(offsetUV)
                        result.assign(result.max(mask))
                    })
                })

            return result
        })

        blurMaterial.fragmentNode = resolve()
        blurMaterial.depthWrite = false

        this.blurPlane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), blurMaterial)
        this.blurPlane.frustumCulled = false
        this.blurPlane.renderOrder = 0
        this.blurPlane.material.depthWrite = false

        this.reset()
    }

    getResolution() {
        return this.resolution
    }

    setResolution(resolution: number) {
        this.resolution = resolution
    }

    getSmoothIterations() {
        return this.smoothIterations
    }

    setSmoothIterations(smoothIterations: number) {
        this.smoothIterations = smoothIterations
    }

    has(mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>) {
        return this.meshes.has(mesh)
    }

    onDisposingMesh(mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>) {
        this.meshes.delete(mesh)
    }

    dispose() {
        for (const oldMesh of this.meshes) {
            oldMesh.geometry.deleteAttribute("uv3")
            this.detachedUVShadowMap.next(oldMesh)
        }
        for (const proxy of this.shadowProxies.values()) proxy.dispose()
        for (const dummyMaterial of Object.values(this.dummyMaterials)) dummyMaterial.dispose()
        for (const shadowMaterial of Object.values(this.shadowMaterials)) shadowMaterial.dispose()
        for (const maskMaterial of Object.values(this.maskMaterials)) maskMaterial.dispose()
        this.debugMaterial.dispose()
        this.dummyRenderTarget.dispose()
        this.shadowMapMask.dispose()
        if (this.currentMask) {
            this.currentMask.dispose()
            this.currentMask = undefined
        }
        if (this.currentMaskTemp) {
            this.currentMaskTemp.dispose()
            this.currentMaskTemp = undefined
        }
        this.uvShadowMap.dispose()
        this.uvShadowMapTemp.dispose()
        this.blurPlane.geometry.dispose()
        this.blurPlane.material.dispose()
        this.meshes.clear()
        if (this.debugObject) {
            this.debugObject.geometry.dispose()
            this.debugObject.material.dispose()
            this.debugObject = undefined
        }
    }

    getUVShadowMap() {
        return this.uvShadowMap.texture
    }

    attachToMeshes(newMeshes: Set<THREE.Mesh<THREE.BufferGeometry, THREE.Material>>) {
        const previousMeshes = this.meshes
        const previousGeometries = new Set([...previousMeshes].map((mesh) => mesh.geometry))
        const claimedGeometries = new Set<THREE.BufferGeometry>()
        const entries: {mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>; uv: THREE.BufferAttribute; analysis: UVGeometryAnalysis}[] = []

        for (const newMesh of newMeshes) {
            const {geometry} = newMesh

            const existingUvs = geometry.getAttribute("uv")
            if (!existingUvs || existingUvs.count === 0) {
                ThreeProgressiveUVShadowMap.logger.warn("No UVs found on mesh, skipping", newMesh)
                continue
            }

            if (claimedGeometries.has(geometry)) {
                ThreeProgressiveUVShadowMap.logger.warn("Geometry is shared with another shadow map mesh, skipping", newMesh)
                continue
            }

            if (geometry.getAttribute("uv3") && !previousGeometries.has(geometry)) {
                ThreeProgressiveUVShadowMap.logger.warn("UV with index 3 already exist on mesh, skipping", newMesh)
                continue
            }

            const index = geometry.getIndex()
            if (!index || index.count === 0 || index.count % 3 !== 0) {
                ThreeProgressiveUVShadowMap.logger.warn("Mesh index is missing or invalid, skipping", newMesh)
                continue
            }

            const {uv, analysis} = getUVGeometryAnalysis(geometry)

            if (analysis.surfaceArea <= 0 || !isFinite(analysis.surfaceArea)) {
                ThreeProgressiveUVShadowMap.logger.warn("Mesh surface is invalid, skipping", newMesh)
                continue
            }

            if (analysis.uvArea <= 0 || !isFinite(analysis.uvArea)) {
                ThreeProgressiveUVShadowMap.logger.warn("Mesh uv surface is invalid, skipping", newMesh)
                continue
            }

            if (analysis.acceptedIslandCount === 0) {
                ThreeProgressiveUVShadowMap.logger.warn("All uv islands of mesh are overlapping or degenerate, skipping", newMesh)
                continue
            }

            if (analysis.acceptedIslandCount < analysis.islands.count) {
                const rejected = analysis.islands.count - analysis.acceptedIslandCount
                ThreeProgressiveUVShadowMap.logger.debug(
                    `${rejected} of ${analysis.islands.count} uv islands are overlapping or degenerate and stay unshadowed`,
                    newMesh,
                )
            }

            claimedGeometries.add(geometry)
            entries.push({mesh: newMesh, uv, analysis})
        }

        const layout = layoutUVAtlas(
            entries.map(({analysis}) => analysis),
            this.resolution,
            islandPaddingTexels(this.smoothIterations),
        )

        entries.forEach(({mesh, uv, analysis}, entry) => {
            if (layout.compact[entry]) ThreeProgressiveUVShadowMap.logger.debug("Too many uv islands, packing mesh compactly", mesh)

            const uv3 = mesh.geometry.getAttribute("uv3")
            const reusable = uv3 instanceof THREE.BufferAttribute && uv3.array instanceof Float32Array && uv3.count === uv.count
            const target = reusable ? (uv3.array as Float32Array) : new Float32Array(uv.count * 2)

            writeAtlasUVs(uv, analysis, getAuthoredMaterialSide(mesh.material), layout, entry, target)

            if (reusable) uv3.needsUpdate = true
            else mesh.geometry.setAttribute("uv3", new THREE.BufferAttribute(target, 2))
        })

        this.meshes = new Set(entries.map(({mesh}) => mesh))

        for (const oldMesh of previousMeshes) {
            if (this.meshes.has(oldMesh)) continue
            if (!claimedGeometries.has(oldMesh.geometry)) oldMesh.geometry.deleteAttribute("uv3")
            this.detachedUVShadowMap.next(oldMesh)
        }
        for (const mesh of this.meshes) if (!previousMeshes.has(mesh)) this.attachedUVShadowMap.next(mesh)

        const resolutionSq = this.resolution * this.resolution
        const dimensionSq = layout.width * layout.height
        const scale = dimensionSq > 0 ? Math.sqrt(resolutionSq / dimensionSq) : 0

        this.resolutionX.value = Math.max(1, Math.round(layout.width * scale))
        this.resolutionY.value = Math.max(1, Math.round(layout.height * scale))

        this.uvShadowMapTemp.setSize(this.resolutionX.value, this.resolutionY.value)
        this.uvShadowMap.setSize(this.resolutionX.value, this.resolutionY.value)
        this.shadowMapMask.setSize(this.resolutionX.value, this.resolutionY.value)
        if (this.currentMask) this.currentMask.setSize(this.resolutionX.value, this.resolutionY.value)
        if (this.currentMaskTemp) this.currentMaskTemp.setSize(this.resolutionX.value, this.resolutionY.value)
        this.shadowMapMaskDirty = true

        this.reset()

        if (this.debugObject) this.debugObject.scale.set(1, this.resolutionY.value / this.resolutionX.value, 1)
    }

    compute(scene: THREE.Scene, currentIteration: number, iterations = 1) {
        if (this.meshes.size === 0) return
        if (this.resolutionX.value <= 0 || this.resolutionY.value <= 0) return

        const tempScene = new THREE.Scene()

        const lightObjects: (THREE.Light | THREE.Mesh)[] = []
        scene.traverseVisible((object) => {
            if (object instanceof THREE.Light) {
                if (object.castShadow) lightObjects.push(object)
            } else if (object instanceof THREE.Mesh && (object.castShadow || object.receiveShadow)) {
                lightObjects.push(object)
            }
        })

        // When the set of shadow lights changes at runtime, three rebuilds its internal lights/shadow nodes, and a
        // reused light proxy carries over state that's now inconsistent with the rebuilt nodes -> its shadow stops
        // re-rendering (renders once, then no jitter / no response to moves). A fresh load works because everything
        // is built clean. So on any light-set change we drop the proxies and rebuild them fresh, matching that path.
        if (currentIteration === 0) {
            const shadowLightKey = lightObjects
                .filter((o): o is THREE.Light => o instanceof THREE.Light)
                .map((light) => light.uuid)
                .sort()
                .join(",")
            if (shadowLightKey !== this.previousShadowLightKey) {
                for (const proxy of this.shadowProxies.values()) proxy.dispose()
                this.shadowProxies.clear()
                this.previousShadowLightKey = shadowLightKey
            }
        }

        const staleProxies = new Set(this.shadowProxies.keys())

        const originalObjectData = new Map<THREE.Object3D, OriginalObjectRenderData>()
        lightObjects.forEach((object) => {
            if (object instanceof THREE.Light) {
                let proxy = this.shadowProxies.get(object)
                if (!proxy) {
                    proxy = createShadowLightProxy(object) ?? undefined
                    if (proxy) this.shadowProxies.set(object, proxy)
                }
                if (proxy) {
                    staleProxies.delete(object)
                    proxy.sync()
                    proxy.addToScene(tempScene)
                    return
                }
            }

            originalObjectData.set(object, getOriginalObjectRenderData(object))
            tempScene.attach(object)
        })

        for (const light of staleProxies) {
            const lightProxy = this.shadowProxies.get(light)
            if (!lightProxy) throw new Error("Reference light not found")
            lightProxy.dispose()
            this.shadowProxies.delete(light)
        }

        if (currentIteration === 0 && this.shadowProxies.size > 0) {
            const sceneBoundingSphere = getSceneBoundingSphere(lightObjects)
            for (const proxy of this.shadowProxies.values()) proxy.configureForScene?.(sceneBoundingSphere)
        }

        const rendererState = THREE.RendererUtils.saveRendererState(this.renderer)
        const oldShadowMapEnabled = this.renderer.shadowMap.enabled

        const originalMeshData = new Map<THREE.Mesh, OriginalMeshRenderData>()
        const originalLightData = new Map<THREE.Light, OriginalLightRenderData>()
        lightObjects.forEach((object) => {
            if (object instanceof THREE.Mesh) {
                const mesh = object as THREE.Mesh
                originalMeshData.set(mesh, getOriginalMeshRenderData(mesh))
            } else if (object instanceof THREE.Light) {
                const light = object as THREE.Light
                originalLightData.set(light, getOriginalLightRenderData(light))
            }
        })

        this.renderer.shadowMap.enabled = true
        this.renderer.setClearColor(0xffffff, 1)

        for (let i = 0; i < iterations; i++) {
            this.iteration.value = currentIteration + i

            //1. Pass updates the internal Three.JS shadow maps using all meshes
            lightObjects.forEach((object) => {
                if (object instanceof THREE.Mesh) {
                    const mesh = object as THREE.Mesh

                    const originalData = originalMeshData.get(mesh)
                    if (!originalData) throw new Error("Original mesh data not found")

                    restoreOriginalMeshRenderData(mesh, originalData)

                    if (Array.isArray(mesh.material)) mesh.material = mesh.material.map((material) => this.dummyMaterials[getAuthoredMaterialSide(material)])
                    else mesh.material = this.dummyMaterials[getAuthoredMaterialSide(mesh.material)]

                    mesh.frustumCulled = false
                }
            })

            tempScene.traverseVisible((object) => {
                if (object instanceof THREE.Light) {
                    const light = object as ThreeLightWithShadow
                    if (light.shadow) {
                        light.shadow.autoUpdate = false
                        light.shadow.needsUpdate = true
                    }
                }
            })

            const [offsetX, offsetY] = getJitterVector(this.iteration.value)

            for (const proxy of this.shadowProxies.values()) proxy.jitter(offsetX, offsetY)

            const [receiverOffsetX, receiverOffsetY] = getReceiverJitterVector(this.iteration.value)
            this.receiverJitter.value.set(receiverOffsetX, receiverOffsetY)

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const {_nodes} = this.renderer as any
            if (!_nodes) throw new Error("Renderer nodes not found")
            const nodeFrame = _nodes.nodeFrame
            nodeFrame.update()
            this.renderer.setRenderTarget(this.dummyRenderTarget)
            this.renderer.clear()
            this.renderer.render(tempScene, this.camera)

            tempScene.traverseVisible((object) => {
                if (object instanceof THREE.Light) {
                    const light = object as ThreeLightWithShadow
                    if (light.shadow) {
                        light.shadow.autoUpdate = false
                        light.shadow.needsUpdate = false
                    }
                }
            })

            //2. Pass updates the uv shadow map with only the meshes that receive shadows
            lightObjects.forEach((object) => {
                if (object instanceof THREE.Mesh) {
                    if (object.receiveShadow && this.meshes.has(object as THREE.Mesh<THREE.BufferGeometry, THREE.Material>)) {
                        const mesh = object as THREE.Mesh<THREE.BufferGeometry, THREE.Material>

                        mesh.material = this.shadowMaterials[getAuthoredMaterialSide(mesh.material)]
                    } else object.visible = false
                }
            })

            const activeMap = this.iteration.value % 2 === 0 ? this.uvShadowMap : this.uvShadowMapTemp
            const inactiveMap = this.iteration.value % 2 === 0 ? this.uvShadowMapTemp : this.uvShadowMap

            this.previousMap.value = inactiveMap.texture

            // Keep the color buffer: under receiver jitter, texels at chart borders flicker in and out of coverage,
            // and a cleared iteration would replace their accumulated history with the clear color
            const previousAutoClear = this.renderer.autoClear
            this.renderer.autoClear = false
            this.renderer.setRenderTarget(activeMap)
            this.renderer.clear(false, true, true)
            this.renderer.render(tempScene, this.camera)
            this.renderer.autoClear = previousAutoClear
        }

        if (this.shadowMapMaskDirty) {
            this.renderer.setClearColor(0x000000, 1)
            this.renderer.setRenderTarget(this.shadowMapMask)

            lightObjects.forEach((object) => {
                if (object instanceof THREE.Mesh) {
                    if (object.receiveShadow && this.meshes.has(object as THREE.Mesh<THREE.BufferGeometry, THREE.Material>)) {
                        const mesh = object as THREE.Mesh<THREE.BufferGeometry, THREE.Material>
                        mesh.material = this.maskMaterials[getAuthoredMaterialSide(mesh.material)]
                    }
                }
            })

            this.renderer.clear()
            this.renderer.render(tempScene, this.camera)
            this.shadowMapMaskDirty = false
        }

        originalMeshData.forEach((originalData, mesh) => restoreOriginalMeshRenderData(mesh, originalData))
        originalLightData.forEach((originalData, light) => restoreOriginalLightRenderData(light, originalData))
        originalObjectData.forEach((originalData, object) => restoreOriginalObjectRenderData(object, originalData))

        const activeMap = this.iteration.value % 2 === 0 ? this.uvShadowMap : this.uvShadowMapTemp
        const inactiveMap = this.iteration.value % 2 === 0 ? this.uvShadowMapTemp : this.uvShadowMap

        this.renderer.copyTextureToTexture(activeMap.texture, inactiveMap.texture)

        tempScene.clear()
        tempScene.add(this.blurPlane)

        this.blendMode.value = 0
        this.previousMask.value = this.shadowMapMask.texture
        this.previousMap.value = this.uvShadowMapTemp.texture

        this.renderer.setRenderTarget(this.uvShadowMap)
        this.renderer.clear()
        this.renderer.render(tempScene, this.camera)

        THREE.RendererUtils.restoreRendererState(this.renderer, rendererState)
        this.renderer.shadowMap.enabled = oldShadowMapEnabled
    }

    smoothResult(currentIteration: number, iterations = 1) {
        if (this.meshes.size === 0) return
        if (this.resolutionX.value <= 0 || this.resolutionY.value <= 0) return

        if (this.shadowMapMaskDirty) return

        if (!this.currentMask) {
            this.currentMask = new THREE.RenderTarget(this.resolutionX.value, this.resolutionY.value, {
                generateMipmaps: false,
                minFilter: THREE.NearestFilter,
                magFilter: THREE.NearestFilter,
                format: THREE.RedFormat,
                colorSpace: THREE.NoColorSpace,
            })
        }

        if (!this.currentMaskTemp) {
            this.currentMaskTemp = new THREE.RenderTarget(this.resolutionX.value, this.resolutionY.value, {
                generateMipmaps: false,
                minFilter: THREE.NearestFilter,
                magFilter: THREE.NearestFilter,
                format: THREE.RedFormat,
                colorSpace: THREE.NoColorSpace,
            })
        }

        const rendererState = THREE.RendererUtils.saveRendererState(this.renderer)
        this.renderer.setClearColor(0x000000, 1)

        const tempScene = new THREE.Scene()
        tempScene.add(this.blurPlane)

        for (let i = 0; i < iterations; i++) {
            this.iteration.value = currentIteration + i

            const activeMap = this.iteration.value % 2 === 0 ? this.uvShadowMap : this.uvShadowMapTemp
            const inactiveMap = this.iteration.value % 2 === 0 ? this.uvShadowMapTemp : this.uvShadowMap

            const activeCurrentMask = this.iteration.value % 2 === 0 ? this.currentMask : this.currentMaskTemp
            const inactiveCurrentMask =
                this.iteration.value === 0 ? this.shadowMapMask : this.iteration.value % 2 === 0 ? this.currentMaskTemp : this.currentMask

            this.blendMode.value = 1
            this.previousMap.value = inactiveMap.texture
            this.previousMask.value = inactiveCurrentMask.texture

            this.renderer.setRenderTarget(activeMap)
            this.renderer.clear()
            this.renderer.render(tempScene, this.camera)

            this.blendMode.value = 2

            this.renderer.setRenderTarget(activeCurrentMask)
            this.renderer.clear()
            this.renderer.render(tempScene, this.camera)
        }

        const activeMap = this.iteration.value % 2 === 0 ? this.uvShadowMap : this.uvShadowMapTemp
        const inactiveMap = this.iteration.value % 2 === 0 ? this.uvShadowMapTemp : this.uvShadowMap

        this.renderer.copyTextureToTexture(activeMap.texture, inactiveMap.texture)

        THREE.RendererUtils.restoreRendererState(this.renderer, rendererState)
    }

    getDebugObject() {
        if (!this.debugObject) {
            const labelMaterial = new THREE.MeshBasicMaterial({map: this.uvShadowMapTemp.texture, side: THREE.DoubleSide})
            const labelPlane = new THREE.PlaneGeometry(100, 100)
            this.debugObject = new THREE.Mesh(labelPlane, labelMaterial)
            this.debugObject.position.y = 250
            if (this.resolutionX.value > 0 && this.resolutionY.value > 0) this.debugObject.scale.set(1, this.resolutionY.value / this.resolutionX.value, 1)
            return this.debugObject
        } else return this.debugObject
    }

    reset() {
        this.fillRenderTargetWithColor(this.uvShadowMapTemp, new THREE.Color(0xffffff))
        this.fillRenderTargetWithColor(this.uvShadowMap, new THREE.Color(0xffffff))
        this.fillRenderTargetWithColor(this.shadowMapMask, new THREE.Color(0xffffff))
        this.shadowMapMaskDirty = true
        if (this.currentMask) this.fillRenderTargetWithColor(this.currentMask, new THREE.Color(0xffffff))
        if (this.currentMaskTemp) this.fillRenderTargetWithColor(this.currentMaskTemp, new THREE.Color(0xffffff))
    }

    private fillRenderTargetWithColor(renderTarget: THREE.RenderTarget, color: THREE.Color) {
        const rendererState = THREE.RendererUtils.saveRendererState(this.renderer)

        this.renderer.setRenderTarget(renderTarget)
        this.renderer.setClearColor(color, 1)
        this.renderer.clear()

        THREE.RendererUtils.restoreRendererState(this.renderer, rendererState)
    }

    private static readonly logger = loggerFactory.getLogger("scene")
}
